import * as React from "react";
import { UitkTertiaryButton } from "uitk-react-button";
import { UitkStepInput } from "uitk-react-step-input";
import { liveAnnounce, Level } from "uitk-react-utils";
import { AgeSelect } from "./AgeSelect";
import { locKeys } from "../l10n";
import { Child, RoomProps, Infant } from "../typings";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { getTracker } from "utils/TrackingUtils";
import { inject } from "mobx-react";

export const Room = inject("analytics")((props: RoomProps) => {
  const roomRef: React.RefObject<HTMLHeadingElement> = React.createRef();
  props.roomRefs.push(roomRef);

  const { formatText } = useLocalization();

  const onUpdateRoomAdults = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            adults: parseInt(event.target.value, 10),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.adults`);
  };

  const onUpdateRoomChildren = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChildrenAdded = parseInt(event.target.value, 10) > props.travelers.rooms[roomIndex].children.length;
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            children: isChildrenAdded
              ? room.children.concat({ age: props.config.minChildAge })
              : room.children.slice(0, -1),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.children`);
  };

  const onUpdateRoomInfants = (roomIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isInfantAdded = parseInt(event.target.value, 10) > props.travelers.rooms[roomIndex].infants.length;
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            infants: isInfantAdded
              ? room.infants.concat({ age: props.config.minInfantAge })
              : room.infants.slice(0, -1),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });

    tracker.trackEvent(`${_guests}${roomIndex}.infants`);
  };

  const onRemoveRoom = (roomIndex: number) => () => {
    props.setTravelers({
      rooms: props.travelers.rooms.filter((room, index) => roomIndex !== index),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.roomRemoved, ""), Level.POLITE);
    props.setTotalRoomRefs(props.totalRoomRefs - 1);

    tracker.trackEvent(`${_guests}remove-room`);
  };

  const onUpdateChildAge = (roomIndex: number, childIndex: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const age = parseInt(event.target.value, 10);
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, i) => {
        if (i === roomIndex) {
          return {
            ...room,
            children: room.children.map((child, j) => {
              if (j === childIndex) {
                return { age };
              }

              return child;
            }),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.childUpdated, childIndex + 1, age), Level.POLITE);
  };

  const onUpdateInfantAge = (roomIndex: number, infantIndex: number) => (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const age = parseInt(event.target.value, 10);
    props.setTravelers({
      rooms: props.travelers.rooms.map((room, i) => {
        if (i === roomIndex) {
          return {
            ...room,
            infants: room.infants.map((infant, j) => {
              if (j === infantIndex) {
                return { age };
              }

              return infant;
            }),
          };
        }

        return room;
      }),
      infantSeating: props.travelers.infantSeating,
    });
    liveAnnounce(formatText(locKeys.infantUpdated, infantIndex + 1, age), Level.POLITE);
  };

  const tracker = getTracker(props.lobState.trackingEnabled, props.analytics, props.lobState.moduleName);
  const _guests = "guests.";

  return (
    <div className="roomPickerRoom all-y-padding-four" data-testid={`room-${props.index + 1}`}>
      {props.config.withRooms && (
        <h3
          className="roomHeader uitk-type-200 uitk-type-medium no-outline"
          aria-label={formatText(locKeys.roomHeaderAriaLabel, props.index + 1, props.travelers.rooms.length)}
          tabIndex={0}
          ref={roomRef}
        >
          <span aria-hidden="true">{formatText(locKeys.roomHeaderTitle, props.index + 1)}</span>
        </h3>
      )}
      <UitkStepInput
        title={formatText(locKeys.adults)}
        increaseText={formatText(locKeys.increaseAdults, props.index + 1)}
        decreaseText={formatText(locKeys.decreaseAdults, props.index + 1)}
        max={props.config.maxAdultsPerRoom}
        min={props.config.minAdultsPerRoom}
        value={props.room.adults}
        onChange={onUpdateRoomAdults(props.index)}
        className="adultStepInput"
        id={`adult-input-${props.index}`}
      />
      <UitkStepInput
        title={formatText(locKeys.children)}
        increaseText={formatText(locKeys.increaseChildren, props.index + 1)}
        decreaseText={formatText(locKeys.decreaseChildren, props.index + 1)}
        caption={formatText(props.config.childrenCaptionToken)}
        max={props.config.maxChildrenPerRoom}
        min={props.config.minChildrenPerRoom}
        value={props.room.children.length}
        onChange={onUpdateRoomChildren(props.index)}
        className="childStepInput"
        id={`child-input-${props.index}`}
      />
      <UitkLayoutGrid space="two" columns={2}>
        {props.room.children.map((child: Child, index: number) => (
          <UitkLayoutGridItem colSpan={1} key={index}>
            <AgeSelect
              index={index}
              label={formatText(locKeys.childAgeLabel, index + 1)}
              guest={child}
              ageOptions={props.childAgeOptions}
              onChange={onUpdateChildAge(props.index, index)}
              id={`child-age-input-${props.index}-${index}`}
            />
          </UitkLayoutGridItem>
        ))}
      </UitkLayoutGrid>
      {props.config.withInfants && (
        <>
          <UitkStepInput
            title={formatText(locKeys.infants)}
            increaseText={formatText(locKeys.increaseInfants, props.index + 1)}
            decreaseText={formatText(locKeys.decreaseInfants, props.index + 1)}
            caption={formatText(props.config.infantsCaptionToken)}
            max={props.config.maxInfantsPerRoom}
            min={props.config.minInfantsPerRoom}
            value={props.room.infants.length}
            onChange={onUpdateRoomInfants(props.index)}
            className="childStepInput"
            id={`infant-input-${props.index}`}
          />
          <UitkLayoutGrid columns={2} space="two">
            {props.room.infants.map((infant: Infant, index: number) => (
              <UitkLayoutGridItem colSpan={1} key={index}>
                <AgeSelect
                  index={index}
                  label={formatText(locKeys.infantAgeLabel, index + 1)}
                  guest={infant}
                  ageOptions={props.infantAgeOptions}
                  onChange={onUpdateInfantAge(props.index, index)}
                  id={`infant-age-input-${props.index}-${index}`}
                />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>
        </>
      )}
      {props.index > 0 && (
        <div className="removeRoomButton uitk-type-right all-t-padding-four">
          <UitkTertiaryButton onClick={onRemoveRoom(props.index)} text={formatText(locKeys.removeRoom)} />
        </div>
      )}
    </div>
  );
});
